import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-solid-svg-icons/faCheck';
import { faTimes } from '@fortawesome/pro-solid-svg-icons/faTimes';
import { faBolt } from '@fortawesome/pro-solid-svg-icons/faBolt';

import React, { useCallback, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { QuickshipSettingsType, QuickshipSettingsDefaultProps } from './prop-types';
import { noop } from 'ui-components/default-props/noop';
import classnames from 'classnames';
import { defineMessages, FormattedMessage } from 'react-intl';
import { CheckboxSlider } from 'ui-components/components/checkbox-slider';
import { Popover } from 'ui-components/components/popover';
import { Link } from 'react-router-dom';
import { UserSettings, quickshipUserSettings } from '../../../enums/user-settings/user-settings';

import { UserSettingsOptions } from '../../../enums/user-settings/user-settings-options';
import { css } from 'emotion';

import style from './style.module.less';

const popoverTipShapeStyles = css`
  fill: rgb(238, 238, 238);
`;
const popoverBodyStyles = css`
  box-shadow: 0 2px 9px 0 rgba(0, 0, 0, 0.28);
`;

const messages = defineMessages({
  header: {
    description: 'Quickship Orders Grid Button Header',
    defaultMessage: 'QuickShip Mode',
    id: '3rWDxL',
  },
  footer: {
    description: 'Quickship Orders Grid Button Footer',
    defaultMessage: 'Adjust QuickShip in <a>Settings</a>',
    id: '19TLYx',
  },
  sliderOff: {
    description: 'Quickship Orders Grid Button Slider Off',
    defaultMessage: 'Off',
    id: 'rp1+Ha',
  },
  sliderOn: {
    description: 'Quickship Orders Grid Button Slider On',
    defaultMessage: 'On',
    id: 'RtSOOf',
  },
  ifNeeded: {
    description: 'if necessary setting text',
    defaultMessage: 'Show Cost Summary If Needed',
    id: 'J3qI6O',
  },
  off: {
    description: 'off label for checkbox slider',
    defaultMessage: 'Off',
    id: 'zJCbtR',
  },
  on: {
    description: 'on label for checkbox slider',
    defaultMessage: 'On',
    id: 'ogtHzP',
  },
});

const settingDisabledDisplay = <span className={style['setting-disabled']}>--</span>;
const quickshipSettingsLink = '/settings/workflow';

const ENABLE_QUICKSHIP_FROM_POPOVER_PENDO_SUFFIX = '#quickship-toggle-on';
const DISABLE_QUICKSHIP_FROM_POPOVER_PENDO_SUFFIX = '#quickship-toggle-off';
const LINK_TO_QUICKSHIP_SETTINGS_FROM_POPOVER_PENDO_SUFFIX = '#adjust-quickship-in-settings';
const QUICKSHIP_BOLT_GRID_BUTTON_PENDO_SUFFIX = '#quickship-lightning-bolt';

// covers non-binary settings (like show cost summary)
const settingDisplayMap = (setting, userSettingsWithValues) => {
  if (setting.key === UserSettings.quickshipShowCostSummary.key) {
    const showCheck = [
      UserSettingsOptions.quickshipAlwaysShowCostSummary.settingValue,
      UserSettingsOptions.quickshipShowCostSummaryIfNeeded.settingValue,
    ].includes(userSettingsWithValues[setting.key]);
    return (
      <React.Fragment key={setting.key}>
        {showCheck ? <FontAwesomeIcon className={style.check} icon={faCheck} /> : settingDisabledDisplay}
        {userSettingsWithValues[setting.key] === UserSettingsOptions.quickshipShowCostSummaryIfNeeded.settingValue ? (
          <FormattedMessage {...messages.ifNeeded} />
        ) : (
          <FormattedMessage {...setting.messageDescriptor} />
        )}
      </React.Fragment>
    );
  }

  return (
    <React.Fragment key={setting.key}>
      {userSettingsWithValues[setting.key] ? <FontAwesomeIcon className={style.check} icon={faCheck} /> : settingDisabledDisplay}
      <FormattedMessage {...setting.messageDescriptor} />
    </React.Fragment>
  );
};

const QuickshipPopoverContent = ({
  closePopover,
  isQuickshipEnabled = false,
  toggleQuickshipEnabled = noop,
  settingsWithStatus = QuickshipSettingsDefaultProps,
  pendoTrackingPrefix,
}) => {
  const pendoTrackingSuffix = isQuickshipEnabled ? DISABLE_QUICKSHIP_FROM_POPOVER_PENDO_SUFFIX : ENABLE_QUICKSHIP_FROM_POPOVER_PENDO_SUFFIX;
  const togglerTrackingId = `${pendoTrackingPrefix}${pendoTrackingSuffix}`;
  const renderSlider = useCallback(() => {
    return (
      <div className={style['slider-row']}>
        <span className={style['slider-label']}>
          <FormattedMessage {...messages.off} />
        </span>
        <CheckboxSlider
          checkboxId='quickship'
          checked={isQuickshipEnabled}
          className={style['slider-styles']}
          onChange={toggleQuickshipEnabled}
          pendoTrackingId={togglerTrackingId}
        />
        <span className={style['slider-label']}>
          <FormattedMessage {...messages.on} />
        </span>
      </div>
    );
  }, [isQuickshipEnabled, toggleQuickshipEnabled, togglerTrackingId]);

  return (
    <div className={style['quickship-popover-content']}>
      <div className={style['header-row']}>
        <FormattedMessage {...messages.header} />
        <span onClick={closePopover}>
          <FontAwesomeIcon className={style['popover-header-close']} icon={faTimes} />
        </span>
      </div>
      <div className={style.content}>
        {renderSlider()}
        <div className={style['settings-title']}>QuickShip Settings:</div>
        <div className={style.settings}>{quickshipUserSettings.map((setting) => settingDisplayMap(setting, settingsWithStatus))}</div>
      </div>
      <div className={style.footer}>
        <FormattedMessage
          {...messages.footer}
          values={{
            a: (chunks) => (
              <Link
                data-pendo-id={`${pendoTrackingPrefix}${LINK_TO_QUICKSHIP_SETTINGS_FROM_POPOVER_PENDO_SUFFIX}`}
                to={quickshipSettingsLink}
              >
                {chunks}
              </Link>
            ),
          }}
        />
      </div>
    </div>
  );
};

QuickshipPopoverContent.propTypes = {
  closePopover: PropTypes.func.isRequired,
  isQuickshipEnabled: PropTypes.bool.isRequired,
  toggleQuickshipEnabled: PropTypes.func.isRequired,
  settingsWithStatus: PropTypes.object.isRequired,
  pendoTrackingPrefix: PropTypes.string.isRequired,
};

export const QuickshipPopover = ({
  isQuickshipEnabled = false,
  toggleQuickshipEnabled = noop,
  settingsWithStatus = QuickshipSettingsDefaultProps,
  pendoTrackingPrefix,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const popoverWrapperRef = useRef(null);
  const targetRef = useRef(null);

  const closePopover = useCallback(() => {
    setIsOpen(false);
  }, [setIsOpen]);

  const togglePopover = useCallback(() => {
    setIsOpen(!isOpen);
  }, [isOpen]);

  const childProps = {
    closePopover,
    isQuickshipEnabled,
    toggleQuickshipEnabled,
    settingsWithStatus,
    pendoTrackingPrefix,
  };

  const props = {
    appendTarget: document.querySelector('#app-root'),
    body: <QuickshipPopoverContent {...childProps} />,
    isOpen,
    onOuterAction: closePopover,
    place: 'below',
    popoverBodyStyles,
    popoverTipShapeStyles,
    ref: popoverWrapperRef,
    tipSize: 15,
  };

  const pendoTrackingId = `${pendoTrackingPrefix}${QUICKSHIP_BOLT_GRID_BUTTON_PENDO_SUFFIX}`;

  return (
    <Popover {...props}>
      <div
        ref={targetRef}
        className={classnames(
          style['quickship-popover'],
          { [style['popover-open']]: isOpen },
          { [style['quickship-enabled']]: isQuickshipEnabled },
        )}
        data-pendo-id={pendoTrackingId}
        onClick={togglePopover}
      >
        <FontAwesomeIcon className={style['quickship-icon']} icon={faBolt} />
      </div>
    </Popover>
  );
};

QuickshipPopover.propTypes = {
  isQuickshipEnabled: PropTypes.bool,
  settingsWithStatus: QuickshipSettingsType,
  toggleQuickshipEnabled: PropTypes.func,
  pendoTrackingPrefix: PropTypes.string.isRequired,
};
